/**
 * WidgetPreview — the single rendering authority for the picker card AND the
 * canvas (ADR-RE-3 / §2.1, Story 37.7).
 *
 * The picker card and the on-canvas widget must be "structurally equivalent at
 * the same zoom/format" (§2.1) — NOT literal-number equivalent. That holds **by
 * construction** here: the preview renders the SAME `<WidgetRenderer>` the
 * canvas renders, just with representative SAMPLE data (instant, stable,
 * cacheable — no network) inside a fixed-size frame scaled with `transform:
 * scale()`. The canvas renders the same component with LIVE data (that is
 * 37.8); because both go through `WidgetRenderer`, the layout/chrome match is
 * automatic.
 *
 * Key invariants:
 * - SAMPLE data only (`generateMockDataForWidget`) — never `/api/reports/populate`
 *   (Pre-mortem B: a live-fetching preview is a request storm).
 * - `isPreviewMode={false}` — the embed widget spins up a live sandboxed iframe
 *   + a 10s timeout when `isPreviewMode` is truthy (correction #14). A
 *   sample-data preview must stay static: no iframe, no network, no timer.
 * - DETERMINISTIC, non-interactive, decorative: `pointer-events:none` +
 *   `aria-hidden` on the scaled render (the card owns the accessible
 *   name + drag affordance). [a11y research 2026-06-01: decorative scaled
 *   content uses aria-hidden + pointer-events:none.]
 */

import { useMemo } from "react";
import type { WidgetConfig } from "../../lib/templateUtils";
import { generateMockDataForWidget } from "../../lib/mockWidgetData";
import { WidgetRenderer } from "./widgets/WidgetRenderer";

export interface WidgetPreviewProps {
  /** The widget to preview (its `id` keys the sample-data map). */
  widget: WidgetConfig;
  /** Scale factor for the fixed-size frame (1 = 100%). Default 1. */
  scale?: number;
  /** Report format — forwarded to WidgetRenderer (e.g. cover-page chrome). */
  format?: "document" | "slide-deck";
  /** Width of the fixed pre-scale frame in px. Default 240. */
  frameWidth?: number;
  /** Height of the fixed pre-scale frame in px. Default 120. */
  frameHeight?: number;
}

export function WidgetPreview({
  widget,
  scale = 1,
  format = "document",
  frameWidth = 240,
  frameHeight = 120,
}: WidgetPreviewProps) {
  // Guard against a 0 / negative / non-finite scale (review #11) collapsing the
  // frame to 0×0 or mirroring it — clamp to a small positive floor.
  const safeScale = Number.isFinite(scale) && scale > 0 ? scale : 1;

  // Sample data keyed by widget.id — WidgetRenderer looks it up by the same id,
  // so the id MUST be stable (callers pass a deterministic `preview-${type}` id).
  const widgetData = useMemo(
    () => ({ [widget.id]: generateMockDataForWidget(widget) }),
    [widget],
  );

  return (
    <div
      data-testid={`widget-preview-frame-${widget.type}`}
      aria-hidden="true"
      className="overflow-hidden"
      style={{
        width: frameWidth * safeScale,
        height: frameHeight * safeScale,
        pointerEvents: "none",
      }}
    >
      <div
        style={{
          width: frameWidth,
          height: frameHeight,
          transform: `scale(${safeScale})`,
          transformOrigin: "top left",
        }}
      >
        <WidgetRenderer
          widget={widget}
          widgetData={widgetData}
          format={format}
          isPreviewMode={false}
        />
      </div>
    </div>
  );
}
